import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Card, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { currentUrl } from "../../App";
import useApp from "../../utils/useApp";
import useHousingInventory from "../../utils/useHousingInventory";
import { NeedsHousingRow, buildNeedsHousingRows, bedsOfRoom, getRoomMode, propertyCounts } from "../../helpers/housingAssignment";
import PageTitle from "../ui/PageTitle";
import SearchBar from "../ui/SearchBar";
import NeedsHousingPanel from "./NeedsHousingPanel";
import DwellingAssignmentRow from "./DwellingAssignmentRow";
import FacilityAssignmentCard from "./FacilityAssignmentCard";
import RoomAssignmentRow from "./RoomAssignmentRow";
import CaseHousingFlow from "../ui/CaseHousingFlow";

interface HousingAssignmentState {
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  getData: boolean;
  errMsg: any;
  setErrMsg: any;
}

const HousingAssignment = ({ setShowToast, setToastText, setToastBody, getData, errMsg, setErrMsg }: HousingAssignmentState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { showMenu }: any = useApp();

  const inventory = useHousingInventory({ withCandidates: true });
  const {
    dwellings,
    facilities,
    rooms,
    beds,
    housingCandidates,
    possibleOccupants,
    housingByCaseId,
    isLoading,
    // a failed load still clears the spinner, so without this the screen would simply look
    // like a shelter with nothing set up in it
    errMsg: inventoryErrMsg,
  } = inventory;

  const [search, setSearch] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [activeCaseId, setActiveCaseId] = useState<number | null>(null);
  const [expandedFacilityId, setExpandedFacilityId] = useState<any>(null);
  const [pickersOpen, setPickersOpen] = useState(0);

  // every open picker is left over from data that has just been replaced
  const getAssignmentData = () => inventory.refresh().then(() => setPickersOpen(0));

  useEffect(() => {
    if (getData) {
      getAssignmentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData]);

  const matchesSearchText = (name: string) => {
    const q = search.trim().toLowerCase();
    return !q || name.toLowerCase().includes(q);
  };

  const roomIsAvailable = (room: any) => {
    const mode = getRoomMode(room, beds);
    return mode !== "family" && bedsOfRoom(beds, room.id).some((b: any) => !b.occupant_id);
  };

  const roomHasBeds = (room: any) => bedsOfRoom(beds, room.id).length > 0;

  const searching = search.trim().length > 0;

  // A case assigned a whole room or dwelling names nobody, so searching one of its people
  // found nothing - match against the case's own people by their plain names. Their
  // possibleOccupants `name` carries a " - Room (Bed)" suffix, which would let a room search
  // masquerade as a person search, so compare the raw first and last name instead.
  const caseHasPersonMatchingSearch = (caseId: any) =>
    !!caseId &&
    possibleOccupants.some((p: any) => p.case_id === caseId && matchesSearchText(`${p.firstname || ""} ${p.lastname || ""}`.trim()));

  // Matches a room's occupant(s) - the family case's name, or an individually-assigned
  // bed's occupant name or case name - so searching a case or person surfaces where they're housed.
  const roomOccupantMatchesSearch = (room: any) => {
    if (room.family_case_id) {
      const familyCase = housingByCaseId.get(room.family_case_id);
      if (!!familyCase && matchesSearchText(familyCase.name)) return true;
      return caseHasPersonMatchingSearch(room.family_case_id);
    }
    return bedsOfRoom(beds, room.id).some((b: any) => {
      if (!b.occupant_id) return false;
      const personName = `${b.firstname || ""} ${b.lastname || ""}`.trim();
      if (personName && matchesSearchText(personName)) return true;
      const occupantCase = b.case_id ? housingByCaseId.get(b.case_id) : undefined;
      return !!occupantCase && matchesSearchText(occupantCase.name);
    });
  };

  const dwellingCounts = (dwelling: any) => propertyCounts(rooms, beds, dwelling.id);

  const needsHousingRows = useMemo(
    () => buildNeedsHousingRows(housingCandidates, possibleOccupants, beds, rooms, facilities),
    [housingCandidates, possibleOccupants, beds, rooms, facilities],
  );

  const activeRow = needsHousingRows.find((r: NeedsHousingRow) => r.caseId === activeCaseId) || null;

  // A dwelling/room with nothing to assign into isn't shown at all - not even under "All".
  const assignableDwellings = useMemo(() => dwellings.filter((d: any) => dwellingCounts(d).bedCount > 0), [dwellings, rooms, beds]);
  const standaloneRooms = useMemo(() => rooms.filter((r: any) => !r.property_id && roomHasBeds(r)), [rooms, beds]);

  const roomMatchesFilters = (room: any) => {
    if (!matchesSearchText(room.name) && !roomOccupantMatchesSearch(room)) return false;
    if (onlyAvailable && !roomIsAvailable(room)) return false;
    return true;
  };

  const dwellingMatchesFilters = (dwelling: any) => {
    const occupantCase = dwelling.occupant_case_id ? housingByCaseId.get(dwelling.occupant_case_id) : undefined;
    const nameOk =
      matchesSearchText(dwelling.name) ||
      (!!occupantCase && matchesSearchText(occupantCase.name)) ||
      caseHasPersonMatchingSearch(dwelling.occupant_case_id);
    if (!nameOk) return false;
    if (onlyAvailable && dwelling.occupant_case_id) return false;
    return true;
  };

  const visibleDwellings = assignableDwellings.filter(dwellingMatchesFilters);
  const visibleStandaloneRooms = standaloneRooms.filter(roomMatchesFilters);

  const visibleFacilities = useMemo(() => {
    return facilities
      .map((facility: any) => {
        const facilityRooms = rooms.filter((r: any) => r.property_id === facility.id && roomHasBeds(r));
        const facilityNameMatches = matchesSearchText(facility.name);
        const visibleRooms = facilityRooms.filter((room: any) => {
          if (!facilityNameMatches && !matchesSearchText(room.name) && !roomOccupantMatchesSearch(room)) return false;
          if (onlyAvailable && !roomIsAvailable(room)) return false;
          return true;
        });
        const { bedCount, occupiedBedCount } = propertyCounts(rooms, beds, facility.id);
        return { facility, facilityRooms, visibleRooms, bedCount, occupiedBedCount };
      })
      .filter((f: any) => f.visibleRooms.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilities, rooms, beds, search, onlyAvailable, housingByCaseId, possibleOccupants]);

  const facilitiesWithAssignableRooms = useMemo(
    () => facilities.filter((facility: any) => rooms.some((r: any) => r.property_id === facility.id && roomHasBeds(r))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facilities, rooms, beds],
  );

  // Housing a case is the same flow the Household tab uses, opened in place of the list.
  const onFindBed = (row: NeedsHousingRow) => {
    setActiveCaseId(row.caseId);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const onCloseHousing = () => {
    setActiveCaseId(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const filtersLocked = pickersOpen > 0;
  const onPickerOpenChange = (open: boolean) => setPickersOpen((n: number) => Math.max(0, n + (open ? 1 : -1)));

  const commonRowProps = {
    facilities,
    housingCandidates,
    housingByCaseId,
    possibleOccupants,
    onAssigned: getAssignmentData,
    onPickerOpenChange,
    setErrMessage: setErrMsg,
    setShowToast,
    setToastText,
    setToastBody,
  };

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : activeRow ? (
        <CaseHousingFlow
          caseId={activeRow.caseId}
          caseName={activeRow.caseName}
          people={possibleOccupants.filter((p: any) => p.case_id === activeRow.caseId)}
          dwellings={dwellings}
          facilities={facilities}
          rooms={rooms}
          beds={beds}
          backLabel="Back to Assignment"
          onClose={onCloseHousing}
          onChanged={getAssignmentData}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : (
        <>
          <p className={errMsg || inventoryErrMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg || inventoryErrMsg}
          </p>
          <PageTitle title="Assignment" type="section" />

          <Row className={showMenu ? "my-3 sticky-top card-note-menu-open-sticky" : "my-3 sticky-top card-note-sticky"}>
            <Col xs={12}>
              <Card className="bg-light">
                <Card.Body className="p-2">
                  <Stack direction="horizontal" gap={3} className="flex-wrap">
                    <SearchBar
                      cssClass="flex-grow-1"
                      placeholder={`Search ${assignableDwellings.length > 0 ? "dwellings, " : ""}${facilitiesWithAssignableRooms.length > 0 ? "facilities, " : ""}rooms, cases, or people`}
                      searchText={search}
                      setSearchText={setSearch}
                      searchLogic={() => {}}
                      disabled={filtersLocked}
                    />
                    <div className="d-flex flex-shrink-0">
                      <Button
                        className={
                          !onlyAvailable ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"
                        }
                        disabled={filtersLocked}
                        onClick={() => {
                          setOnlyAvailable(false);
                          setExpandedFacilityId(null);
                        }}
                      >
                        All
                      </Button>
                      <Button
                        className={onlyAvailable ? "app-primary-color button rounded" : "app-primary-color-inverse button rounded"}
                        disabled={filtersLocked}
                        onClick={() => {
                          setOnlyAvailable(true);
                          setExpandedFacilityId(null);
                        }}
                      >
                        Available
                      </Button>
                    </div>
                  </Stack>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <NeedsHousingPanel rows={needsHousingRows} onFindBed={onFindBed} />

          {assignableDwellings.length > 0 && (
            <div className="mb-4">
              <h5 className="app-header-text">Dwellings</h5>
              {visibleDwellings.length === 0 ? (
                <p className="text-muted small">No dwellings match the current filters.</p>
              ) : (
                <Stack gap={2}>
                  {visibleDwellings.map((dwelling: any) => {
                    const { roomCount, bedCount } = dwellingCounts(dwelling);
                    return (
                      <DwellingAssignmentRow
                        key={dwelling.id}
                        dwelling={dwelling}
                        dwellings={dwellings}
                        rooms={rooms}
                        beds={beds}
                        roomCount={roomCount}
                        bedCount={bedCount}
                        {...commonRowProps}
                      />
                    );
                  })}
                </Stack>
              )}
            </div>
          )}

          {facilitiesWithAssignableRooms.length > 0 && (
            <div className="mb-4">
              <h5 className="app-header-text">Facilities</h5>
              {visibleFacilities.length === 0 ? (
                <p className="text-muted small">No facilities match the current filters.</p>
              ) : (
                <Stack gap={2}>
                  {visibleFacilities.map(({ facility, facilityRooms, visibleRooms, bedCount, occupiedBedCount }: any) => (
                    <FacilityAssignmentCard
                      key={facility.id}
                      facility={facility}
                      rooms={visibleRooms}
                      allRooms={rooms}
                      totalRoomCount={facilityRooms.length}
                      totalBedCount={bedCount}
                      occupiedBedCount={occupiedBedCount}
                      expanded={searching || expandedFacilityId === facility.id}
                      onToggleExpanded={() => setExpandedFacilityId((current: any) => (current === facility.id ? null : facility.id))}
                      beds={beds}
                      {...commonRowProps}
                    />
                  ))}
                </Stack>
              )}
            </div>
          )}

          {standaloneRooms.length > 0 && (
            <div className="mb-4">
              <h5 className="app-header-text">Standalone Rooms</h5>
              {visibleStandaloneRooms.length === 0 ? (
                <p className="text-muted small">No standalone rooms match the current filters.</p>
              ) : (
                <Stack gap={2}>
                  {visibleStandaloneRooms.map((room: any) => (
                    <RoomAssignmentRow key={room.id} room={room} rooms={rooms} beds={beds} {...commonRowProps} />
                  ))}
                </Stack>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default HousingAssignment;
